import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bank, LinkSimple, ArrowClockwise, PlugsConnected, Plugs, ArrowUp, ArrowDown, Warning } from "@phosphor-icons/react";
import api from "../lib/api";

function money(v, ccy = "AUD") {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: ccy || "AUD" }).format(n);
}

export default function BankAccounts() {
  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ["bank-status"],
    queryFn: async () => (await api.get("/bank/status")).data,
  });
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const connect = useMutation({
    mutationFn: async () => (await api.post("/bank/connect-url")).data,
    onSuccess: (r) => {
      window.open(r.url, "_blank", "noopener");
      toast.info("Complete the connection in the Basiq tab, then click 'Load accounts'.");
      refetchStatus();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not start the bank connection"),
  });

  const refresh = useMutation({
    mutationFn: async () => (await api.post("/bank/refresh")).data,
    onSuccess: (r) => {
      setAccounts(r.accounts || []);
      setTransactions(r.transactions || []);
      setLoaded(true);
      refetchStatus();
      if ((r.accounts || []).length === 0) toast.info("No linked accounts yet. Finish connecting your bank, then try again.");
      else toast.success(`Loaded ${r.accounts.length} account(s)`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not load bank data"),
  });

  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/bank/disconnect")).data,
    onSuccess: () => { setAccounts([]); setTransactions([]); setLoaded(false); refetchStatus(); toast.success("Bank disconnected"); },
    onError: () => toast.error("Could not disconnect"),
  });

  const connected = status?.has_user;
  const notConfigured = status && !status.configured;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="bank-accounts-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight mt-1">Bank Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Securely link your company's Australian bank accounts via open banking (Basiq / CDR) to see live balances and recent transactions.
            Your bank credentials are entered on your bank's own secure page — never stored by Neuclius.
          </p>
        </div>
        <button
          onClick={() => refresh.mutate()}
          disabled={!connected || refresh.isPending}
          data-testid="bank-refresh"
          className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-sm border border-border text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <ArrowClockwise size={15} weight="bold" className={refresh.isPending ? "animate-spin" : ""} />
          {refresh.isPending ? "Loading…" : "Load accounts"}
        </button>
      </div>

      {notConfigured && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4 text-sm flex items-start gap-2" data-testid="bank-not-configured">
          <Warning size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <span>Open banking isn't configured yet. Add a <span className="font-mono">BASIQ_API_KEY</span> to enable live connections.</span>
        </div>
      )}

      {/* Connection status */}
      <div className="border border-border rounded-sm bg-card p-5 flex items-center justify-between gap-4" data-testid="bank-status-card">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded-full flex items-center justify-center ${connected ? "bg-emerald-500/10" : "bg-secondary"}`}>
            {connected ? <PlugsConnected size={22} weight="fill" className="text-emerald-600" /> : <Plugs size={22} className="text-muted-foreground" />}
          </div>
          <div>
            <p className="font-bold text-sm" data-testid="bank-status-label">{connected ? "Bank connection active" : "No bank connected"}</p>
            <p className="text-xs text-muted-foreground">
              {connected
                ? `Linked under ${status?.email || "your company"}${status?.accounts_count ? ` · ${status.accounts_count} account(s)` : ""}`
                : "Connect your bank to pull balances and transactions."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => connect.mutate()}
            disabled={connect.isPending || notConfigured}
            data-testid="bank-connect-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <LinkSimple size={15} weight="bold" />
            {connect.isPending ? "Opening…" : connected ? "Link another bank" : "Connect bank account"}
          </button>
          {connected && (
            <button
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              data-testid="bank-disconnect-btn"
              className="h-9 px-3 rounded-sm border border-border text-sm font-semibold hover:bg-secondary disabled:opacity-50"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>

      {/* Accounts */}
      {accounts.length > 0 && (
        <div className="space-y-3" data-testid="bank-accounts-list">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Linked Accounts</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {accounts.map((a) => (
              <div key={a.id} className="border border-border rounded-sm bg-card p-4" data-testid="bank-account-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm">{a.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{a.accountNo}</p>
                    {a.institution && <p className="text-[11px] text-muted-foreground mt-0.5">{a.institution}</p>}
                  </div>
                  {a.class && <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-sm bg-secondary">{a.class}</span>}
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="overline-label">Balance</p>
                    <p className="text-2xl font-black tabular">{money(a.balance, a.currency)}</p>
                  </div>
                  {a.availableFunds != null && (
                    <div className="text-right">
                      <p className="overline-label">Available</p>
                      <p className="text-sm font-semibold tabular">{money(a.availableFunds, a.currency)}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transactions */}
      {transactions.length > 0 && (
        <div className="space-y-3" data-testid="bank-transactions">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Recent Transactions</h2>
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border bg-secondary/40">
                  <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Date</th>
                  <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Description</th>
                  <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Status</th>
                  <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => {
                  const credit = t.direction === "credit";
                  return (
                    <tr key={t.id} className="border-b border-border/60" data-testid="bank-txn-row">
                      <td className="py-2.5 px-4 whitespace-nowrap text-muted-foreground">{(t.postDate || "").slice(0, 10) || "—"}</td>
                      <td className="py-2.5 px-4 max-w-[380px] truncate">{t.description || "—"}</td>
                      <td className="py-2.5 px-4"><span className="text-xs text-muted-foreground capitalize">{t.status || ""}</span></td>
                      <td className={`py-2.5 px-4 text-right tabular font-semibold ${credit ? "text-emerald-600" : "text-foreground"}`}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          {credit ? <ArrowDown size={13} weight="bold" /> : <ArrowUp size={13} weight="bold" />}
                          {money(t.amount)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {connected && loaded && accounts.length === 0 && (
        <div className="border border-border rounded-sm p-8 text-center text-sm text-muted-foreground" data-testid="bank-empty">
          No accounts loaded yet. If you just connected, finish the steps in the Basiq tab, then click <span className="font-semibold">Load accounts</span>.
        </div>
      )}
    </div>
  );
}
